import { createFileRoute } from '@tanstack/react-router';
import { Boxes } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { ComponentTabs } from '@/components/glitch-ui/component-tabs';

export const Route = createFileRoute('/_authenticated/_layout/components/colors')({
  component: ColorsPage,
  staticData: {
    title: 'Components',
    icon: Boxes,
  },
});

function ColorsPage() {
  return (
    <div className="flex flex-col h-full">
      <ComponentTabs />

      <div className="flex-1 pb-8 space-y-8">
        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Primary Brand Colors</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="rounded-lg border bg-white">
              <CardContent className="p-6">
                <div className="h-32 rounded-lg bg-[#290BD9] mb-4 flex items-center justify-center">
                  <p className="text-white font-semibold">Cobalt</p>
                </div>
                <p className="text-gray-900 font-semibold mb-1">#290BD9</p>
                <p className="text-sm text-gray-700">Primary brand color</p>
              </CardContent>
            </Card>

            <Card className="rounded-lg border bg-white">
              <CardContent className="p-6">
                <div className="h-32 rounded-lg bg-[#D6CDEE] mb-4 flex items-center justify-center">
                  <p className="text-gray-900 font-semibold">Violet Haze</p>
                </div>
                <p className="text-gray-900 font-semibold mb-1">#D6CDEE</p>
                <p className="text-sm text-gray-700">SMB tier accent</p>
              </CardContent>
            </Card>

            <Card className="rounded-lg border bg-white">
              <CardContent className="p-6">
                <div className="h-32 rounded-lg bg-[#CED6EF] mb-4 flex items-center justify-center">
                  <p className="text-gray-900 font-semibold">Soft Blue</p>
                </div>
                <p className="text-gray-900 font-semibold mb-1">#CED6EF</p>
                <p className="text-sm text-gray-700">Agency tier accent</p>
              </CardContent>
            </Card>

            <Card className="rounded-lg border bg-white">
              <CardContent className="p-6">
                <div className="h-32 rounded-lg bg-[#E6CDED] mb-4 flex items-center justify-center">
                  <p className="text-gray-900 font-semibold">Quartz Pink</p>
                </div>
                <p className="text-gray-900 font-semibold mb-1">#E6CDED</p>
                <p className="text-sm text-gray-700">Enterprise tier accent</p>
              </CardContent>
            </Card>
          </div>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Brand Gradients</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="rounded-lg border bg-white">
              <CardContent className="p-6">
                <div className="h-40 rounded-lg mb-4 flex items-center justify-center" style={{ background: 'linear-gradient(215deg, #F7F7F7 35%, #D6CDEE 100%)' }}>
                  <p className="text-gray-900 font-semibold">SMB</p>
                </div>
                <p className="text-gray-900 font-semibold mb-2">SMB Gradient</p>
                <p className="text-sm text-gray-700 mb-2">Cloudline → Violet Haze</p>
                <p className="text-xs text-gray-500">Use: SMB tier cards, small business features</p>
              </CardContent>
            </Card>

            <Card className="rounded-lg border bg-white">
              <CardContent className="p-6">
                <div className="h-40 rounded-lg mb-4 flex items-center justify-center" style={{ background: 'linear-gradient(215deg, #F7F7F7 35%, #CED6EF 100%)' }}>
                  <p className="text-gray-900 font-semibold">Agency</p>
                </div>
                <p className="text-gray-900 font-semibold mb-2">Agency Gradient</p>
                <p className="text-sm text-gray-700 mb-2">Cloudline → Soft Blue</p>
                <p className="text-xs text-gray-500">Use: Agency tier cards, collaboration features</p>
              </CardContent>
            </Card>

            <Card className="rounded-lg border bg-white">
              <CardContent className="p-6">
                <div className="h-40 rounded-lg mb-4 flex items-center justify-center" style={{ background: 'linear-gradient(215deg, #F7F7F7 35%, #E6CDED 100%)' }}>
                  <p className="text-gray-900 font-semibold">Enterprise</p>
                </div>
                <p className="text-gray-900 font-semibold mb-2">Enterprise Gradient</p>
                <p className="text-sm text-gray-700 mb-2">Cloudline → Quartz Pink</p>
                <p className="text-xs text-gray-500">Use: Enterprise tier cards, premium features</p>
              </CardContent>
            </Card>
          </div>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Semantic Colors</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="rounded-lg border bg-white">
              <CardContent className="p-6">
                <div className="h-20 rounded-lg mb-3 flex items-center justify-center border" style={{ backgroundColor: '#FEE2E2', borderColor: '#FCA5A5' }}>
                  <p className="font-semibold" style={{ color: '#B91C1C' }}>Error</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-gray-700"><strong>Text:</strong> #B91C1C</p>
                  <p className="text-sm text-gray-700"><strong>Background:</strong> #FEE2E2</p>
                  <p className="text-sm text-gray-700"><strong>Border:</strong> #FCA5A5</p>
                </div>
                <p className="text-xs text-gray-500 mt-2">Complementary to brand, 6.71:1 contrast</p>
              </CardContent>
            </Card>

            <Card className="rounded-lg border bg-white">
              <CardContent className="p-6">
                <div className="h-20 rounded-lg mb-3 flex items-center justify-center border" style={{ backgroundColor: '#D1FAE5', borderColor: '#6EE7B7' }}>
                  <p className="font-semibold" style={{ color: '#047857' }}>Success</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-gray-700"><strong>Text:</strong> #047857</p>
                  <p className="text-sm text-gray-700"><strong>Background:</strong> #D1FAE5</p>
                  <p className="text-sm text-gray-700"><strong>Border:</strong> #6EE7B7</p>
                </div>
                <p className="text-xs text-gray-500 mt-2">Analogous teal, 5.77:1 contrast</p>
              </CardContent>
            </Card>

            <Card className="rounded-lg border bg-white">
              <CardContent className="p-6">
                <div className="h-20 rounded-lg mb-3 flex items-center justify-center border" style={{ backgroundColor: '#FEF3C7', borderColor: '#FDE68A' }}>
                  <p className="font-semibold" style={{ color: '#A16207' }}>Warning</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-gray-700"><strong>Text:</strong> #A16207</p>
                  <p className="text-sm text-gray-700"><strong>Background:</strong> #FEF3C7</p>
                  <p className="text-sm text-gray-700"><strong>Border:</strong> #FDE68A</p>
                </div>
                <p className="text-xs text-gray-500 mt-2">Traditional yellow-based, 4.76:1 contrast</p>
              </CardContent>
            </Card>

            <Card className="rounded-lg border bg-white">
              <CardContent className="p-6">
                <div className="h-20 rounded-lg mb-3 flex items-center justify-center border" style={{ backgroundColor: '#EDE9FE', borderColor: '#C4B5FD' }}>
                  <p className="font-semibold" style={{ color: '#290BD9' }}>Info</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-gray-700"><strong>Text:</strong> #290BD9</p>
                  <p className="text-sm text-gray-700"><strong>Background:</strong> #EDE9FE</p>
                  <p className="text-sm text-gray-700"><strong>Border:</strong> #C4B5FD</p>
                </div>
                <p className="text-xs text-gray-500 mt-2">Primary brand color for info and links</p>
              </CardContent>
            </Card>
          </div>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Status Pills & Badges</h2>
          <Card className="rounded-lg border bg-white">
            <CardContent className="p-6">
              <p className="text-gray-900 mb-4">Pills and badges use the pattern: <code className="px-2 py-1 bg-gray-100 rounded text-sm">inline-flex items-center px-3.5 py-0.5 rounded-full text-xs font-medium</code></p>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center px-3.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-900">Neutral</span>
                  <span className="text-sm text-gray-700">bg-gray-100 text-gray-900</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center px-3.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Critical</span>
                  <span className="text-sm text-gray-700">bg-red-100 text-red-800</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center px-3.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">Seasonal</span>
                  <span className="text-sm text-gray-700">bg-orange-100 text-orange-800</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center px-3.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">Warning</span>
                  <span className="text-sm text-gray-700">bg-yellow-100 text-yellow-800</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center px-3.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Success</span>
                  <span className="text-sm text-gray-700">bg-green-100 text-green-800</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center px-3.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">Standard</span>
                  <span className="text-sm text-gray-700">bg-blue-100 text-blue-800</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center px-3.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">Onboarding</span>
                  <span className="text-sm text-gray-700">bg-indigo-100 text-indigo-800</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center px-3.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">Brand</span>
                  <span className="text-sm text-gray-700">bg-purple-100 text-purple-800</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center px-3.5 py-0.5 rounded-full text-xs font-medium bg-pink-100 text-pink-800">Featured</span>
                  <span className="text-sm text-gray-700">bg-pink-100 text-pink-800</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center px-3.5 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-teal-800">Active</span>
                  <span className="text-sm text-gray-700">bg-teal-100 text-teal-800</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Neutral Colors</h2>
          <Card className="rounded-lg border bg-white">
            <CardContent className="p-6 space-y-3">
              <div className="flex items-center gap-4">
                <div className="h-12 w-24 rounded-md bg-white border"></div>
                <div className="flex-1">
                  <p className="text-gray-900 font-semibold">bg-white (#FFFFFF)</p>
                  <p className="text-sm text-gray-700">Card backgrounds, content areas</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="h-12 w-24 rounded-md bg-gray-50 border"></div>
                <div className="flex-1">
                  <p className="text-gray-900 font-semibold">bg-gray-50 (#F7F7F7 Cloudline)</p>
                  <p className="text-sm text-gray-700">Brand neutral grey - Overridden to Cloudline for brand consistency</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="h-12 w-24 rounded-md bg-gray-200 border"></div>
                <div className="flex-1">
                  <p className="text-gray-900 font-semibold">border-gray-200 (#E5E7EB)</p>
                  <p className="text-sm text-gray-700">Borders, dividers</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="h-12 w-24 rounded-md bg-gray-500"></div>
                <div className="flex-1">
                  <p className="text-gray-900 font-semibold">text-gray-500 (#6B7280)</p>
                  <p className="text-sm text-gray-700">Muted text, help text, timestamps</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="h-12 w-24 rounded-md bg-gray-700"></div>
                <div className="flex-1">
                  <p className="text-gray-900 font-semibold">text-gray-700 (#374151)</p>
                  <p className="text-sm text-gray-700">Secondary text, form labels</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="h-12 w-24 rounded-md bg-gray-900"></div>
                <div className="flex-1">
                  <p className="text-gray-900 font-semibold">text-gray-900 (#0f172a Dark Slate)</p>
                  <p className="text-sm text-gray-700">Primary text - Overridden to Dark Slate for brand alignment</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>
      </div>
    </div>
  );
}
